import GlobalSearch from "./GlobalSearch";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Activity, LayoutDashboard, Globe, Settings, Bell, LogOut, X, ShieldCheck, Sun, Moon, Share2, FileBarChart, Menu, PanelLeftClose, PanelLeftOpen, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { fmtDate } from "../lib/monitor";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Avatar, AvatarFallback } from "./ui/avatar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/incidents", label: "Incidents", icon: AlertTriangle },
  { to: "/websites", label: "Webapps", icon: Globe },
  { to: "/status-page", label: "Status Page", icon: Share2 },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/settings", label: "Settings", icon: Settings },
];

const SEV_COLOR = {
  critical: "border-l-red-500",
  warning: "border-l-amber-500",
  info: "border-l-emerald-500",
};

function AlertsSheet() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = alerts.filter((a) => !a.read).length;

  const openAndRead = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try { await api.post("/alerts/read-all"); setAlerts(prev => prev.map(a => ({...a, read:true}))); } catch { /* Keep unread state on failure. */ }
    }
  };

  return (
    <Sheet open={open} onOpenChange={openAndRead}>
      <SheetTrigger asChild>
        <button
          aria-label="Open notifications"
          data-testid="alerts-bell-btn"
          aria-label="Open notifications"
          className="relative flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
        >
          <Bell size={18} strokeWidth={2} />
          {unread > 0 && (
            <span
              data-testid="alerts-unread-count"
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-mono font-semibold text-white"
            >
              {unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md p-0" data-testid="alerts-panel">
        <SheetHeader className="border-b border-slate-200 p-5">
          <SheetTitle className="font-heading text-lg flex items-center gap-2">
            <Bell size={18} /> Alerts
          </SheetTitle>
        </SheetHeader>
        <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 80px)" }}>
          {alerts.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No alerts yet. All quiet.</div>
          ) : (
            alerts.map((a) => (
              <div
                key={a.id}
                data-testid="alert-item"
                className={cn("border-l-4 border-b border-slate-100 px-5 py-4", SEV_COLOR[a.severity] || "border-l-slate-300")}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-wide text-slate-500">
                    {a.type.replaceAll("_", " ")}
                  </span>
                  <span className="text-[11px] text-slate-400">{fmtDate(a.created_at)}</span>
                </div>
                <p className="mt-1 text-sm text-slate-700 leading-snug">{a.message}</p>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ThemeToggle() {
  const [dark, setDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem("theme", dark ? "dark" : "light"); } catch { /* Storage is optional. */ }
  }, [dark]);
  return (
    <button
      onClick={() => setDark((d) => !d)}
      data-testid="theme-toggle-btn"
      title={dark ? "Switch to light" : "Switch to dark"}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
    >
      {dark ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
    </button>
  );
}

function NavItems({ onNavigate, items }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          title={label}
          onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase()}`}
          className={({ isActive }) =>
            cn(
              "om-nav-item flex items-center gap-3 px-3 py-3 text-sm font-medium",
              isActive
                ? "bg-blue-50/80 text-blue-700"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            )
          }
        >
          <Icon size={18} strokeWidth={2} />
          <span className="mf-nav-label">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/monitorflux-mf-original.png" alt="" className="h-9 w-14 shrink-0 object-contain" />
      <span className="font-heading text-lg font-bold tracking-tight text-slate-900">MonitorFlux</span>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();
  const navItems = ["admin", "superadmin"].includes(user?.role)
    ? [...NAV, { to: "/admin", label: "Admin", icon: ShieldCheck }]
    : NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className={`om-shell min-h-screen bg-slate-50 ${collapsed ? "mf-collapsed" : ""}`}>
      <a href="#main-content" className="mf-skip">Skip to content</a>
      {/* Sidebar (desktop) */}
      <aside className="om-sidebar fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-slate-200 bg-white px-4 py-6 lg:flex">
        <div className="px-2">
          <Logo />
        </div>
        <button aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} onClick={()=>setCollapsed(v=>!v)} className="mt-6 p-2 rounded-lg text-slate-500 hover:bg-slate-100 self-start">{collapsed ? <PanelLeftOpen size={18}/> : <PanelLeftClose size={18}/>}</button>
        <div className="mt-8">
          <NavItems items={navItems} />
        </div>
      </aside>

      {/* Header */}
      <header className="om-topbar sticky top-0 z-20 border-b border-slate-200 lg:pl-60">
        <div className="flex min-h-16 items-center justify-between gap-2 px-3 sm:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile nav */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button aria-label="Open navigation menu" className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 hover:bg-slate-100 lg:hidden" data-testid="mobile-menu-btn">
                  <Menu size={21} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-5">
                <div className="mb-6">
                  <Logo />
                </div>
                <NavItems items={navItems} onNavigate={()=>setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="lg:hidden max-[410px]:[&_.font-heading]:hidden">
              <Logo />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <GlobalSearch />
            <ThemeToggle />
            <AlertsSheet />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-100 transition-colors"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-100 text-xs font-semibold text-blue-700">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden text-sm font-medium text-slate-700 sm:block">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings size={15} className="mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-600">
                  <LogOut size={15} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main id="main-content" className="min-w-0 lg:pl-60" tabIndex={-1}>
        <div className="mx-auto w-full max-w-[1760px] min-w-0 px-3 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-9 2xl:px-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -5 }}
              transition={{ duration: reduceMotion ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
        <footer className="mt-4 border-t border-slate-200 py-6 text-center" data-testid="app-footer">
          <p className="text-xs text-slate-400">
            Product By{" "}
            <a
              href="https://www.omaxtech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
              data-testid="omaxtech-link"
            >
              Omaxtech Technologies
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}
